import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { RootState } from '../store/store';
import { setCredentials, clearAuth, setLoading, setUser } from '../store/slices/authSlice';
import {
    useLogin,
    useGetCurrentUser,
    useRefreshToken
} from '../services/authService';
import type { Login } from '../services/schemas/authServiceSchemas';
import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient();

export const useAuth = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useSelector((state: RootState) => state.auth);
    const refreshAttemptedRef = useRef(false);
    const hasAttemptedInitialAuthRef = useRef(false);

    const loginMutation = useLogin({
        mutation: {
            onSuccess: (response) => {
                if (response) {
                    dispatch(setCredentials(response));
                    const from = (location.state as any)?.from?.pathname || '/dashboard';
                    navigate(from, { replace: true });
                }
            },
        },
    });

    const refreshMutation = useRefreshToken({
        mutation: {
            onSuccess: (response) => {
                if (response) {
                    dispatch(setCredentials(response));
                    // Fetch user data after successful refresh
                    refetchUser();
                }
            },
            onError: () => {
                handleLogout();
            }
        }
    });

    const {
        data: userData,
        isError: isUserError,
        refetch: refetchUser
    } = useGetCurrentUser({
        query: {
            enabled: false,
            retry: false,
            staleTime: 5 * 60 * 1000,
            gcTime: 10 * 60 * 1000
        }
    });

    // Single effect to handle auth initialization and user data
    useEffect(() => {
        const initAuth = async () => {
            // Skip if we've already attempted initial auth
            if (hasAttemptedInitialAuthRef.current) {
                return;
            }

            // Skip if we're on the login page
            if (location.pathname === '/auth-signin') {
                dispatch(setLoading(false));
                hasAttemptedInitialAuthRef.current = true;
                return;
            }

            // If we have a user, we're already initialized
            if (auth.user) {
                dispatch(setLoading(false));
                hasAttemptedInitialAuthRef.current = true;
                return;
            }

            // If we have an access token but no user
            if (auth.accessToken && !auth.user) {
                try {
                    let result = await refetchUser();

                    // The profile carries the user's groups, and without it every permission
                    // check fails. A single cancelled call is worth retrying before giving up.
                    if (!result.data) {
                        result = await refetchUser();
                    }

                    if (result.data) {
                        dispatch(setUser(result.data));
                        dispatch(setLoading(false));
                        hasAttemptedInitialAuthRef.current = true;
                    } else if (auth.refreshToken && !refreshAttemptedRef.current) {
                        // The token may simply have expired - refresh and let its handler
                        // fetch the profile again, keeping the app in its loading state.
                        refreshAttemptedRef.current = true;
                        refreshMutation.mutate({
                            data: { refreshToken: auth.refreshToken }
                        });
                    } else {
                        // Signed in but with no profile: nothing in the app will open, so end
                        // the session rather than leaving the user stuck with no permissions.
                        handleLogout();
                    }
                } catch (error) {
                    // Only attempt refresh once
                    if (auth.refreshToken && !refreshAttemptedRef.current) {
                        refreshAttemptedRef.current = true;
                        refreshMutation.mutate({
                            data: { refreshToken: auth.refreshToken }
                        });
                    } else {
                        handleLogout();
                    }
                }
            } else {
                dispatch(setLoading(false));
                hasAttemptedInitialAuthRef.current = true;
            }
        };

        initAuth();
    }, [auth.accessToken, auth.user]);

    const handleLogin = async (credentials: Login) => {
        try {
            refreshAttemptedRef.current = false;
            await loginMutation.mutateAsync({ data: credentials });
            return true;
        } catch (error) {
            console.error('Login failed:', error);
            return false;
        }
    };

    const handleLogout = useCallback(() => {
        refreshAttemptedRef.current = false;
        hasAttemptedInitialAuthRef.current = false;
        queryClient.clear();
        dispatch(clearAuth());
        navigate('/auth-signin', { replace: true });
    }, [dispatch, navigate]);

    return {
        user: auth.user,
        isAuthenticated: auth.isAuthenticated,
        isLoading: auth.isLoading || loginMutation.isPending || refreshMutation.isPending,
        login: handleLogin,
        logout: handleLogout,
        loginError: loginMutation.error,
    };
};
